// Portal layout — the client-facing "Septima Link" shell (PRD #71).
//
// Deliberately different chrome from the internal frontends: a top nav strip,
// no sidebar, client-facing branding. Wraps the route tree in a
// PortalSessionProvider and gates the authenticated screens on a live portal
// credential session — an unauthenticated visit redirects to the login page.
// The login page itself (`/portal/login` and any per-client credential link
// `/portal/<slug>`) is reachable without a session so the client can enter
// their password.

import { Link, Navigate, Outlet, useLocation } from 'react-router';
import { cn } from '@/lib/utils';
import Image from '@/lib/Image';
import { getSeptimaMarkPath } from '@/lib/logos';
import { useTheme } from '@/lib/ThemeContext';
import { PortalSessionProvider, usePortalSession } from '@/lib/portal-session';

const NAV: Array<{ href: string; label: string }> = [
    { href: '/portal/dashboard', label: 'Overview' },
    { href: '/portal/portfolio', label: 'Portfolio' },
    { href: '/portal/cash', label: 'Cash' },
    { href: '/portal/exposure', label: 'Exposure' },
    { href: '/portal/documents', label: 'Documents' },
];

// The authenticated portal screens — everything else under `/portal/*` (the
// bare `/portal`, `/portal/login`, and the dynamic `/portal/<slug>` credential
// links) renders the login page and is reachable without a session.
const GATED_PORTAL_PATHS = [
    '/portal/dashboard',
    '/portal/portfolio',
    '/portal/holdings',
    '/portal/cash',
    '/portal/exposure',
    '/portal/documents',
];

function PortalChrome() {
    const { pathname } = useLocation();
    const { session, loading, logout } = usePortalSession();
    const { resolvedTheme } = useTheme();

    const isGated = GATED_PORTAL_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));
    const isLogin = !isGated;

    if (loading && !isLogin) {
        return (
            <div
                className="flex items-center justify-center h-full text-body text-[rgb(var(--color-text-secondary))]"
                role="status"
                aria-live="polite"
            >
                Loading your portal…
            </div>
        );
    }

    if (!session && !isLogin) {
        return <Navigate to="/portal/login" replace />;
    }

    // Bare shell for the login page — no nav, no client identity.
    if (isLogin) {
        return (
            <div className="min-h-full bg-[rgb(var(--color-canvas))]">
                <Outlet />
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden bg-[rgb(var(--color-canvas))]">
            <header className="flex items-center gap-6 px-page-padding h-16 border-b border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-secondary))]">
                <Link to="/portal/dashboard" className="flex items-center gap-2.5">
                    <Image
                        src={getSeptimaMarkPath(resolvedTheme)}
                        alt="Septima"
                        height={28}
                        className="h-7 w-auto"
                        priority
                    />
                    <div>
                        <div className="text-card-title text-[rgb(var(--color-text-primary))]">
                            Septima Link
                        </div>
                        <div className="text-overline uppercase text-[rgb(var(--color-text-secondary))]">
                            Client portal
                        </div>
                    </div>
                </Link>
                <nav className="flex items-center gap-1 ml-8" aria-label="Portal sections">
                    {NAV.map((n) => {
                        const active = pathname === n.href || pathname.startsWith(n.href + '/');
                        return (
                            <Link
                                key={n.href}
                                to={n.href}
                                aria-current={active ? 'page' : undefined}
                                className={cn(
                                    'px-3 h-segmented flex items-center rounded-button text-annotation transition-colors',
                                    active
                                        ? 'bg-[rgb(var(--color-accent)/0.15)] text-[rgb(var(--color-accent))]'
                                        : 'text-[rgb(var(--color-text-secondary))] hover:text-[rgb(var(--color-text-primary))]',
                                )}
                            >
                                {n.label}
                            </Link>
                        );
                    })}
                </nav>
                <div className="ml-auto flex items-center gap-4">
                    <span className="text-annotation text-[rgb(var(--color-text-secondary))]">
                        Signed in as{' '}
                        <span className="text-[rgb(var(--color-text-primary))]">
                            {session?.clientName}
                        </span>
                    </span>
                    <button
                        type="button"
                        onClick={() => {
                            void logout();
                        }}
                        className="px-3 h-segmented rounded-button text-annotation border border-[rgb(var(--color-divider))] text-[rgb(var(--color-text-secondary))] hover:text-[rgb(var(--color-text-primary))] hover:border-[rgb(var(--color-text-secondary))] transition-colors"
                    >
                        Sign out
                    </button>
                </div>
            </header>

            {/* tabIndex 0: the scroll region must be reachable by keyboard so
          keyboard-only users can scroll long portal pages (WCAG 2.1.1). */}
            <main id="portal-main" className="flex-1 overflow-y-auto" tabIndex={0}>
                <Outlet />
            </main>
        </div>
    );
}

export function PortalLayoutRoute() {
    return (
        <PortalSessionProvider>
            <PortalChrome />
        </PortalSessionProvider>
    );
}
